'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
// Replaced lucide-react icons with emoji to avoid Runtime ReferenceError
import UserRankBadge from '@/components/UserRankBadge'

interface Announcement {
  id: string
  title: string
  content: string
  type: 'info' | 'warning' | 'success' | 'important'
  isActive: boolean
  priority: number
  expiresAt?: string
  createdBy: {
    name: string
    username: string
    userRank: string
  }
  createdAt: string
  updatedAt: string
}

type SortField = 'date' | 'priority' | 'type' | 'title'
type SortDirection = 'asc' | 'desc'
type FilterType = 'all' | 'info' | 'warning' | 'success' | 'important'

const ANNOUNCEMENT_TYPES = [
  { value: 'all', label: 'All Types', icon: '🔍', color: 'gray' },
  { value: 'info', label: 'Information', icon: 'ℹ️', color: 'blue' },
  { value: 'warning', label: 'Warning', icon: '⚠️', color: 'yellow' },
  { value: 'success', label: 'Success', icon: '✅', color: 'green' },
  { value: 'important', label: 'Important', icon: '❗', color: 'red' }
] as const

const SORT_OPTIONS = [
  { value: 'date', label: 'Date' },
  { value: 'priority', label: 'Priority' },
  { value: 'type', label: 'Type' },
  { value: 'title', label: 'Title' }
] as const

export default function AnnouncementsPage() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [filteredAnnouncements, setFilteredAnnouncements] = useState<Announcement[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [sortField, setSortField] = useState<SortField>('date')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [filterType, setFilterType] = useState<FilterType>('all')
  const [showSortDropdown, setShowSortDropdown] = useState(false)

  useEffect(() => {
    fetchAnnouncements()
  }, [])

  useEffect(() => {
    filterAndSortAnnouncements()
  }, [announcements, sortField, sortDirection, filterType])

  const fetchAnnouncements = async () => {
    setIsLoading(true)
    try {
      const response = await fetch('/api/announcements/public')
      if (response.ok) {
        const data = await response.json()
        setAnnouncements(data)
      }
    } catch (error) {
      console.error('Error fetching announcements:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const filterAndSortAnnouncements = () => {
    let filtered = [...announcements]

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(announcement => announcement.type === filterType)
    }

    // Sort announcements
    filtered.sort((a, b) => {
      let comparison = 0

      switch (sortField) {
        case 'date':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          break
        case 'priority':
          comparison = a.priority - b.priority
          break
        case 'type':
          comparison = a.type.localeCompare(b.type)
          break
        case 'title':
          comparison = a.title.localeCompare(b.title)
          break
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })

    setFilteredAnnouncements(filtered)
  }

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
    setShowSortDropdown(false)
  }

  const getTypeConfig = (type: string) => {
    return ANNOUNCEMENT_TYPES.find(t => t.value === type) || ANNOUNCEMENT_TYPES[0]
  }

  const isExpired = (expiresAt?: string) => {
    return expiresAt && new Date(expiresAt) < new Date()
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-gradient-to-r from-primary-900/40 to-primary-800/40 rounded-lg border border-primary-600/50">
            <span className="h-8 w-8 text-primary-400 text-2xl flex items-center justify-center">📢</span>
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Announcements</h1>
            <p className="text-gray-300">Stay updated with the latest news from the MathLeet team</p>
          </div>
        </div>

        {/* Professional Sort and Filter Bar */}
        <div className="bg-dark-800/50 backdrop-blur-sm border border-dark-600 rounded-xl p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-2">
              {ANNOUNCEMENT_TYPES.map(type => {
                const isActive = filterType === type.value
                
                return (
                  <button
                    key={type.value}
                    onClick={() => setFilterType(type.value as FilterType)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? `bg-${type.color}-900/30 border border-${type.color}-600/50 text-${type.color}-300 shadow-lg`
                        : 'bg-dark-700/50 border border-dark-600 text-gray-400 hover:text-gray-200 hover:border-dark-500'
                    }`}
                  >
                    <span className="h-4 w-4">{type.icon}</span>
                    <span>{type.label}</span>
                    {type.value !== 'all' && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                        isActive ? 'bg-current/20' : 'bg-gray-600'
                      }`}>
                        {announcements.filter(a => a.type === type.value).length}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>

            {/* Sort Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className="flex items-center gap-2 px-4 py-2 bg-dark-700/50 border border-dark-600 rounded-lg text-gray-300 hover:text-gray-100 hover:border-dark-500 transition-all duration-200 min-w-[140px] justify-between"
              >
                <div className="flex items-center gap-2">
                  {sortDirection === 'asc' ? (
                    <span className="h-4 w-4">🔼</span>
                  ) : (
                    <span className="h-4 w-4">🔽</span>
                  )}
                  <span className="text-sm font-medium">
                    {SORT_OPTIONS.find(opt => opt.value === sortField)?.label}
                  </span>
                </div>
                <span className={`h-4 w-4 transition-transform duration-200 ${
                  showSortDropdown ? 'rotate-180' : ''
                }`}>⌄</span>
              </button>

              {showSortDropdown && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-dark-800 border border-dark-600 rounded-lg shadow-xl z-10">
                  <div className="p-2">
                    {SORT_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        onClick={() => handleSort(option.value as SortField)}
                        className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors ${
                          sortField === option.value
                            ? 'bg-primary-900/30 text-primary-300'
                            : 'text-gray-300 hover:bg-dark-700 hover:text-gray-100'
                        }`}
                      >
                        <span>{option.label}</span>
                        {sortField === option.value && (
                          sortDirection === 'asc' ? (
                            <span className="h-3 w-3">🔼</span>
                          ) : (
                            <span className="h-3 w-3">🔽</span>
                          )
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Results Counter */}
          <div className="mt-3 pt-3 border-t border-dark-700">
            <p className="text-sm text-gray-400">
              Showing {filteredAnnouncements.length} of {announcements.length} announcements
              {filterType !== 'all' && (
                <span> • Filtered by <span className="text-gray-300 font-medium">{
                  ANNOUNCEMENT_TYPES.find(t => t.value === filterType)?.label
                }</span></span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Announcements List */}
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="card p-6 animate-pulse">
              <div className="bg-dark-600 h-6 w-3/4 rounded mb-4"></div>
              <div className="bg-dark-600 h-4 w-full rounded mb-2"></div>
              <div className="bg-dark-600 h-4 w-2/3 rounded"></div>
            </div>
          ))}
        </div>
      ) : filteredAnnouncements.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="mb-6">
            {filterType === 'all' ? (
              <span className="h-16 w-16 text-gray-400 mx-auto text-6xl flex items-center justify-center">📢</span>
            ) : (
              <span className="text-6xl text-gray-400 mx-auto flex items-center justify-center h-16 w-16">
                {getTypeConfig(filterType).icon}
              </span>
            )}
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">
            {filterType === 'all' ? 'No Announcements' : `No ${getTypeConfig(filterType).label} Announcements`}
          </h3>
          <p className="text-gray-300">
            {filterType === 'all' 
              ? 'There are no announcements at the moment. Check back later for updates!'
              : `There are no ${getTypeConfig(filterType).label.toLowerCase()} announcements currently.`
            }
          </p>
          {filterType !== 'all' && (
            <button
              onClick={() => setFilterType('all')}
              className="mt-4 btn btn-secondary"
            >
              View All Announcements
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {filteredAnnouncements.map((announcement) => {
            const typeConfig = getTypeConfig(announcement.type)
            const expired = isExpired(announcement.expiresAt)

            return (
              <Link 
                key={announcement.id} 
                href={`/announcements/${announcement.id}`}
                className={`card p-6 block hover:bg-dark-700/50 transition-colors ${expired ? 'opacity-75' : ''}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg bg-${typeConfig.color}-900/30 border border-${typeConfig.color}-600/50 flex-shrink-0`}>
                    <span className={`text-xl text-${typeConfig.color}-400 flex items-center justify-center h-6 w-6`}>
                      {typeConfig.icon}
                    </span>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-white mb-2 hover:text-primary-400 transition-colors">
                          {announcement.title}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-gray-400 mb-3">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 bg-${typeConfig.color}-900/30 border border-${typeConfig.color}-600/50 rounded text-${typeConfig.color}-300 text-xs font-medium`}>
                              {typeConfig.label}
                            </span>
                            <span className="px-2 py-1 bg-dark-700 border border-dark-600 rounded text-xs">
                              Priority {announcement.priority}
                            </span>
                            {expired && (
                              <span className="px-2 py-1 bg-orange-900/30 border border-orange-600/50 rounded text-orange-300 text-xs">
                                Expired
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="prose prose-invert max-w-none mb-4">
                      <p className="text-gray-200 whitespace-pre-wrap">
                        {announcement.content}
                      </p>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-dark-600">
                      <div className="flex items-center gap-4 text-sm text-gray-400">
                        <div className="flex items-center gap-2">
                          <span className="h-4 w-4">👤</span>
                          <span>{announcement.createdBy.name}</span>
                          {announcement.createdBy.userRank !== 'User' && (
                            <UserRankBadge rank={announcement.createdBy.userRank} size="sm" showLabel={false} />
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="h-4 w-4">📅</span>
                          <span>{new Date(announcement.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}</span>
                        </div>
                        {announcement.expiresAt && (
                          <div className="flex items-center gap-2">
                            <span className="h-4 w-4">⏰</span>
                            <span>
                              Expires {new Date(announcement.expiresAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      )}

      {/* Click outside handler for dropdown */}
      {showSortDropdown && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setShowSortDropdown(false)}
        />
      )}
    </div>
  )
}